import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, Mail, Lock, User as UserIcon, Eye, EyeOff, AtSign, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTermsModal } from '../contexts/TermsModalContext';
import SanctuaryModerationDialog, { parseSanctuaryError } from './SanctuaryModerationDialog';
import { checkHandleAvailable, checkEmailAvailable, getMe } from '../lib/api';
import { subscribeAuthEvents, AUTH_EVENT_EMAIL_VERIFIED } from '../lib/authBroadcast';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// RELIGION_OPTIONS previously fed a picker on the register form.
// The picker is now hidden (product decision — the compose form
// stays focused on essentials). The list is preserved in Settings
// under the hidden Profile picker so the schema stays intact if the
// field is ever re-exposed.

const GENDER_OPTIONS = [
  { value: 'unspecified', label: 'Prefer not to say' },
  { value: 'female', label: 'Female (she/her)' },
  { value: 'male', label: 'Male (he/him)' },
];

const AuthModal = () => {
  const { isAuthModalOpen, closeAuthModal, login, register, resendVerification, authModalInitialMode, refresh } = useAuth();
  const { openTerms } = useTermsModal();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [gender, setGender] = useState('unspecified');
  // Simplified binary Faith field — "non_christian" default. Optional
  // at sign-up; users can flip it in Settings later.
  const [religion, setReligion] = useState('non_christian');
  // Optional but recommended user-chosen handle at signup. Follows
  // the same rules as Settings > Handle: lowercase alphanumeric +
  // underscore, 3-30 chars. Live availability check hits the same
  // `/auth/handle-available` endpoint the Settings page uses. Empty
  // handle is allowed — server auto-generates one from the display
  // name/email so the account is still @-addressable.
  const [handle, setHandle] = useState('');
  const [handleStatus, setHandleStatus] = useState('idle'); // 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  const [handleMsg, setHandleMsg] = useState('');
  const handleCheckSeqRef = useRef(0);
  // Live email availability check on the register form. Same pattern as
  // the handle check — debounced 300ms, last-write-wins seq counter,
  // status feeds inline UI + submit-time guard. Only runs in register
  // mode so login doesn't hit the endpoint on every keystroke.
  const [emailStatus, setEmailStatus] = useState('idle'); // 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  const [emailMsg, setEmailMsg] = useState('');
  const emailCheckSeqRef = useRef(0);
  // `religion` state removed — the register form no longer exposes
  // the picker. The backend defaults new accounts to "christianity"
  // (a product decision; hidden Profile picker preserves the schema).
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showRegisterHint, setShowRegisterHint] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Sanctuary Moderation dialog state — shown when register rejects the
  // display name for church-inappropriate content.
  const [moderationDialog, setModerationDialog] = useState(null);
  // Email-verification hand-off state. Set to the pending email when:
  //   • The user completes registration (backend didn't auto-login —
  //     they need to click the emailed link)
  //   • The user tries to log in but their account isn't verified yet
  // While non-null, the modal renders the "check your email" panel
  // instead of the login/register form.
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const emailRef = useRef(null);
  // Auto-focus target for register mode — first field in the register
  // form is the display-name input (above the email row), so we need
  // a separate ref to focus it on modal open. In login mode we still
  // focus `emailRef`.
  const nameRef = useRef(null);
  // Modal container ref — used by the focus-trap keydown handler to
  // enumerate focusable descendants and wrap Tab / Shift+Tab so
  // keyboard users can't tab out of the modal into the background
  // page they can't see.
  const modalRef = useRef(null);

  // Cross-tab verification handoff. When the "Check your email" panel
  // is showing, the user typically clicks the link in their mail
  // client which opens Tab B (`/verify-email`). Without any signalling
  // the two tabs never sync — Tab A stays on the stale "check your
  // inbox" copy while Tab B shows a success page. To avoid that:
  //   1. Subscribe to BroadcastChannel `pnt-auth` — Tab B posts a
  //      message immediately after `POST /verify-email` succeeds.
  //   2. Poll `GET /api/auth/me` every 3s as a safety net for users
  //      who opened the verify link in a different browser (private
  //      window, another device, etc.) where BroadcastChannel can't
  //      reach.
  // Either signal triggers: refresh auth state → close the modal.
  // The AuthContext then hydrates the user and the app re-renders
  // with the user signed in (backend also set the auth cookie on
  // successful verify, so `getMe()` returns the freshly-verified
  // user).
  useEffect(() => {
    if (!pendingVerificationEmail) return undefined;
    let cancelled = false;
    const handleVerified = async () => {
      if (cancelled) return;
      try {
        // Pull the freshly-verified user into the AuthContext so the
        // rest of the app renders as logged-in without the user
        // touching anything else.
        if (typeof refresh === 'function') await refresh();
      } catch (err) { /* if refresh fails we still want to close */ }
      if (cancelled) return;
      // Clear the panel state and dismiss the modal — user lands on
      // the app already signed in.
      setPendingVerificationEmail('');
      setResendMsg('');
      closeAuthModal();
    };
    // (1) BroadcastChannel listener — instant, same-browser sibling tab.
    const unsubscribe = subscribeAuthEvents((msg) => {
      if (msg?.type === AUTH_EVENT_EMAIL_VERIFIED) handleVerified();
    });
    // (2) Polling safety net — 3s cadence, cheap round-trip.
    const pollId = setInterval(async () => {
      try {
        const me = await getMe();
        if (me?.email_verified) handleVerified();
      } catch (err) {
        /* 401 while unauthenticated is normal — swallow and wait */
      }
    }, 3000);
    return () => {
      cancelled = true;
      unsubscribe();
      clearInterval(pollId);
    };
  }, [pendingVerificationEmail, refresh, closeAuthModal]);

  // Reset fields when opening/closing
  useEffect(() => {
    if (isAuthModalOpen) {
      // Honour the caller-requested pane. Header's "Sign In" opens on
      // 'login'; "Create Account" opens directly on 'register'. Falls
      // back to 'login' when nothing was requested.
      setMode(authModalInitialMode === 'register' ? 'register' : 'login');
      setError('');
      setShowRegisterHint(false);
      setSubmitting(false);
      setPendingVerificationEmail('');
      setResendMsg('');
      setResending(false);
      // Auto-focus the first input for the current mode. The 150ms
      // wait clears the modal's fade-in animation AND lets Radix/
      // portal shifts settle so the browser doesn't reassign focus
      // to the close button afterwards. In register mode the first
      // field is Display name; in login mode it's Email.
      const target = authModalInitialMode === 'register' ? nameRef : emailRef;
      setTimeout(() => target.current?.focus(), 150);
    } else {
      setEmail('');
      setPassword('');
      setName('');
      setHandle('');
      setHandleStatus('idle');
      setHandleMsg('');
      setEmailStatus('idle');
      setEmailMsg('');
      setGender('unspecified');
      setReligion('non_christian');
      setShowPassword(false);
      setShowRegisterHint(false);
      setAcceptedTerms(false);
    }
  }, [isAuthModalOpen, authModalInitialMode]);

  // Handle input normalizer + debounced availability check. The
  // normalizer strips leading `@`, lowercases, and drops any chars
  // that aren't `[a-z0-9_]` — same rules as Settings, so a user who
  // types "@ Alice.T" ends up saving "alicet" cleanly.
  const normalizeHandle = (raw) =>
    (raw || '').replace(/^@+/, '').toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 30);
  const isValidHandle = (h) => /^[a-z0-9_]{3,30}$/.test(h);
  const onChangeHandle = (raw) => {
    const clean = normalizeHandle(raw);
    setHandle(clean);
    if (!clean) {
      // Empty is OK — server will auto-generate. Reset any prior state.
      setHandleStatus('idle');
      setHandleMsg('');
      return;
    }
    if (!isValidHandle(clean)) {
      setHandleStatus('invalid');
      setHandleMsg('3-30 letters, numbers, or _');
      return;
    }
    // Bump the seq so any earlier in-flight check ignores its own
    // response (last-write-wins over a fast typist).
    handleCheckSeqRef.current += 1;
    const seq = handleCheckSeqRef.current;
    setHandleStatus('checking');
    setHandleMsg('Checking availability…');
    // 300ms debounce — enough to avoid hammering the endpoint on every
    // keystroke, tight enough that the user gets feedback before they
    // reach the password field.
    setTimeout(async () => {
      if (seq !== handleCheckSeqRef.current) return;
      try {
        const resp = await checkHandleAvailable(clean);
        if (seq !== handleCheckSeqRef.current) return;
        if (resp?.available) {
          setHandleStatus('available');
          setHandleMsg('Available');
        } else {
          setHandleStatus('taken');
          // `reason` narrows the client-side message. Server may
          // return: 'invalid' (format), 'reserved' (on the reserved
          // list), or 'taken' (already claimed).
          if (resp?.reason === 'invalid') {
            setHandleMsg('3-30 letters, numbers, or _');
          } else if (resp?.reason === 'reserved') {
            setHandleMsg('Currently unavailable');
          } else {
            setHandleMsg('Already taken');
          }
        }
      } catch (err) {
        if (seq !== handleCheckSeqRef.current) return;
        // Network / server hiccup — don't block the user; server will
        // re-validate at submit time.
        console.warn('handle availability check failed', err);
        setHandleStatus('idle');
        setHandleMsg('');
      }
    }, 300);
  };

  // Debounced email availability probe. Only used in register mode —
  // in login mode we skip the check because a "taken" email is
  // actually the required state to sign in successfully. Format is
  // validated cheaply here (permissive regex + length) so obvious
  // typos short-circuit before hitting the network.
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const onChangeEmail = (raw) => {
    const value = (raw || '').trim();
    setEmail(value);
    setShowRegisterHint(false);
    if (mode !== 'register') {
      // Login path: no availability check. Just clear any stale
      // register-mode status the user might have seen before
      // switching modes.
      setEmailStatus('idle');
      setEmailMsg('');
      return;
    }
    if (!value) {
      setEmailStatus('idle');
      setEmailMsg('');
      return;
    }
    if (value.length > 254 || !EMAIL_RE.test(value.toLowerCase())) {
      setEmailStatus('invalid');
      setEmailMsg('Enter a valid email address');
      return;
    }
    emailCheckSeqRef.current += 1;
    const seq = emailCheckSeqRef.current;
    setEmailStatus('checking');
    setEmailMsg('Checking…');
    setTimeout(async () => {
      if (seq !== emailCheckSeqRef.current) return;
      try {
        const resp = await checkEmailAvailable(value.toLowerCase());
        if (seq !== emailCheckSeqRef.current) return;
        if (resp?.available) {
          setEmailStatus('available');
          setEmailMsg('Available');
        } else if (resp?.reason === 'invalid') {
          setEmailStatus('invalid');
          setEmailMsg('Enter a valid email address');
        } else {
          setEmailStatus('taken');
          setEmailMsg('This email is already registered');
        }
      } catch (err) {
        if (seq !== emailCheckSeqRef.current) return;
        // Network hiccup — don't block. Register endpoint will still
        // 409 on collision.
        console.warn('email availability check failed', err);
        setEmailStatus('idle');
        setEmailMsg('');
      }
    }, 300);
  };

  // Escape closes + Tab focus trap. Keeps keyboard users inside the
  // modal until they explicitly close it — otherwise Tab would move
  // focus into the background page (site logo, search, cards) which
  // is visually inaccessible behind the backdrop.
  useEffect(() => {
    if (!isAuthModalOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        closeAuthModal();
        return;
      }
      if (e.key !== 'Tab' || !modalRef.current) return;
      // Collect every currently-focusable descendant. `:not([disabled])`
      // matters because the submit button is disabled until terms are
      // accepted — including it would trap focus on a dead target.
      // `:not([tabindex="-1"])` respects our intentional skip of the
      // show/hide password toggle.
      const focusable = Array.from(
        modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('tabindex') !== '-1');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !modalRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !modalRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isAuthModalOpen, closeAuthModal]);

  if (!isAuthModalOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowRegisterHint(false);
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      setError('Please enter your email and password.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (mode === 'register' && !acceptedTerms) {
      setError('You must accept the terms & conditions to create an account.');
      return;
    }
    if (mode === 'register' && !name.trim()) {
      setError('Please enter a display name.');
      return;
    }
    if (mode === 'register') {
      if (emailStatus === 'checking') {
        setError('Hang on — still checking your email.');
        return;
      }
      if (emailStatus === 'invalid') {
        setError('Please enter a valid email address.');
        return;
      }
      if (emailStatus === 'taken') {
        setError('This email is already registered. Try signing in instead.');
        setShowRegisterHint(false);
        return;
      }
    }
    if (mode === 'register' && handle) {
      if (handleStatus === 'checking') {
        setError('Hang on — still checking your handle.');
        return;
      }
      if (handleStatus === 'invalid' || handleStatus === 'taken') {
        setError('Please pick a different handle.');
        return;
      }
    }
    if (mode === 'register' && !handle) {
      setError('Please choose a handle.');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(trimmedEmail, password);
      } else {
        await register(trimmedEmail, password, {
          name: name.trim() || undefined,
          // Empty handle → server auto-generates. Only forward when the
          // user actually typed one AND our client-side status agrees
          // it's usable.
          handle: (handle && handleStatus !== 'invalid' && handleStatus !== 'taken') ? handle : undefined,
          gender,
          religion,
        });
        // Registration does NOT auto-log in. Show the "check your email"
        // panel inside the modal (rather than closing) so the user has
        // a clear next step. `setPendingVerificationEmail` triggers the
        // panel render — see the JSX below.
        setPendingVerificationEmail(trimmedEmail);
        setSubmitting(false);
        return;
      }
      closeAuthModal();
      // Mobile scroll fix: after a successful account creation the
      // AuthModal closes and the underlying page (usually Home) is
      // revealed — but on iOS/Android the scroll position from wherever
      // the modal was opened stays put, so the user lands mid-page with
      // the header off-screen. Snap the window back to the top on both
      // login and registration success — feels like a fresh session
      // start regardless of device. Deferred to the next tick so the
      // modal's own scroll-lock cleanup runs first.
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          try {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          } catch (_) {
            // Safari <15 doesn't support behavior:'instant' — fall back.
            window.scrollTo(0, 0);
          }
        }, 0);
      }
    } catch (err) {
      // Sanctuary Moderation rejections come back as 422 with a structured
      // detail — surface them via the standard church-sanctuary dialog
      // so the user sees the same warning we show for every other blocked
      // content path in the app.
      const sanctuary = parseSanctuaryError(err);
      if (sanctuary) {
        setModerationDialog(sanctuary);
        setSubmitting(false);
        return;
      }
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Backend returns 403 with `{error_code: "email_not_verified"}`
      // when an unverified user tries to log in. Switch to the "check
      // your email" panel with a "Resend link" affordance so the user
      // can continue without leaving the modal.
      if (status === 403 && detail?.error_code === 'email_not_verified') {
        setPendingVerificationEmail(detail.email || trimmedEmail);
        return;
      }
      setError(formatApiErrorDetail(detail) || err.message || 'Sign-in failed.');
      // Offer a one-tap switch to Sign up on any failed sign-in. The
      // backend now returns a uniform 401 for bad email OR password
      // (enumeration hardening), so we can no longer key this off 404.
      if (mode === 'login' && (status === 401 || status === 404)) setShowRegisterHint(true);
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = () => {
    setMode((m) => (m === 'login' ? 'register' : 'login'));
    setError('');
    setShowRegisterHint(false);
    setAcceptedTerms(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-4 sm:py-8"
      onClick={closeAuthModal}
      data-testid="auth-modal-overlay"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)] flex flex-col rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl overflow-hidden"
        data-testid="auth-modal"
      >
        <div className="flex-shrink-0 flex items-center justify-between px-6 pt-5 pb-3">
          <div>
            <div className="text-xl font-semibold text-zinc-900 dark:text-white" data-testid="auth-modal-title">
              {mode === 'login' ? 'Sign-in or Create account' : 'Create your account'}
            </div>
            <div className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
              {mode === 'login' ? 'Sign in to create and share.' : 'A few seconds to get started.'}
            </div>
          </div>
          <button
            type="button"
            onClick={closeAuthModal}
            data-testid="auth-modal-close"
            className="p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {pendingVerificationEmail ? (
          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-8 pt-2 space-y-4"
            data-testid="auth-verify-email-panel"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center text-amber-600 dark:text-amber-400 text-2xl">
              ✉︎
            </div>
            <h3 className="text-lg font-semibold text-center">Check your email</h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 text-center leading-relaxed">
              We sent a confirmation link to <span className="font-medium text-zinc-900 dark:text-zinc-100">{pendingVerificationEmail}</span>. Click it within an hour to activate your account.
            </p>
            {/* Small ambient hint so users understand this tab will
                auto-advance once they click the link elsewhere —
                otherwise they'd wonder why we're not doing anything
                while they wait for the email. */}
            <div
              className="flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-500"
              data-testid="auth-waiting-for-verification"
            >
              <Loader2 size={12} className="animate-spin text-amber-500" />
              <span>Waiting for you to confirm — this page will update automatically.</span>
            </div>
            <p className="text-xs text-zinc-500 dark:text-zinc-500 text-center">
              Didn&apos;t get it? Check spam, or resend below.
            </p>
            {resendMsg && (
              <p className="text-xs text-center text-emerald-600 dark:text-emerald-400" data-testid="auth-resend-status">{resendMsg}</p>
            )}
            <button
              type="button"
              disabled={resending}
              onClick={async () => {
                setResending(true);
                setResendMsg('');
                try {
                  await resendVerification(pendingVerificationEmail);
                  setResendMsg('Sent — check your inbox again.');
                } catch (err) {
                  setResendMsg('Could not resend right now. Please try again shortly.');
                } finally {
                  setResending(false);
                }
              }}
              data-testid="auth-resend-verification-btn"
              className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold transition-colors"
            >
              {resending ? 'Resending…' : 'Resend verification link'}
            </button>
            <button
              type="button"
              onClick={() => {
                // Force the modal into login mode — the button label
                // says "Back to sign in", so honour that regardless of
                // whether the user reached this panel via the login or
                // register flow.
                setMode('login');
                setPendingVerificationEmail('');
                setResendMsg('');
              }}
              data-testid="auth-back-to-signin-btn"
              className="w-full h-10 rounded-xl border border-zinc-300 dark:border-zinc-700 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
            >
              Back to sign in
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-8 pt-2 space-y-3">
          {mode === 'register' && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Display name</label>
              <div className="flex items-center h-11 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 focus-within:border-amber-500 dark:focus-within:border-amber-400 overflow-hidden">
                <UserIcon size={18} className="ml-3 text-zinc-400" />
                <input
                  ref={nameRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  type="text"
                  autoComplete="name"
                  required
                  placeholder="Your name"
                  data-testid="auth-input-name"
                  className="flex-1 bg-transparent outline-none px-3 text-[15px] text-zinc-900 dark:text-white placeholder-zinc-400"
                />
              </div>
            </div>
          )}

          {mode === 'register' && (
            <div className="space-y-1" data-testid="auth-handle-row">
              <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400" htmlFor="auth-input-handle">
                Handle
              </label>
              <div
                className={`flex items-center h-11 rounded-xl border bg-white dark:bg-zinc-950 overflow-hidden ${
                  handleStatus === 'available'
                    ? 'border-emerald-400 dark:border-emerald-500'
                    : handleStatus === 'invalid' || handleStatus === 'taken'
                    ? 'border-rose-400 dark:border-rose-500'
                    : 'border-zinc-300 dark:border-zinc-700 focus-within:border-amber-500 dark:focus-within:border-amber-400'
                }`}
              >
                <AtSign size={18} className="ml-3 text-zinc-400" />
                <input
                  id="auth-input-handle"
                  value={handle}
                  onChange={(e) => onChangeHandle(e.target.value)}
                  type="text"
                  autoComplete="username"
                  spellCheck={false}
                  autoCapitalize="off"
                  required
                  placeholder="yourhandle"
                  maxLength={30}
                  data-testid="auth-input-handle"
                  className="flex-1 bg-transparent outline-none px-3 text-[15px] text-zinc-900 dark:text-white placeholder-zinc-400 lowercase"
                />
                {handleStatus === 'checking' && (
                  <Loader2 size={16} className="mr-3 animate-spin text-zinc-400" data-testid="auth-handle-checking" />
                )}
                {handleStatus === 'available' && (
                  <Check size={16} className="mr-3 text-emerald-500" data-testid="auth-handle-available" />
                )}
                {(handleStatus === 'invalid' || handleStatus === 'taken') && (
                  <AlertCircle size={16} className="mr-3 text-rose-500" data-testid="auth-handle-error" />
                )}
              </div>
              <div
                className={`text-[11px] min-h-[14px] ${
                  handleStatus === 'available'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : handleStatus === 'invalid' || handleStatus === 'taken'
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-zinc-500 dark:text-zinc-400'
                }`}
                data-testid="auth-handle-status"
              >
                {handleMsg || '3-30 letters, numbers, or _.'}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Email</label>
            <div
              className={`flex items-center h-11 rounded-xl border bg-white dark:bg-zinc-950 overflow-hidden ${
                mode === 'register' && emailStatus === 'available'
                  ? 'border-emerald-400 dark:border-emerald-500'
                  : mode === 'register' && (emailStatus === 'invalid' || emailStatus === 'taken')
                  ? 'border-rose-400 dark:border-rose-500'
                  : 'border-zinc-300 dark:border-zinc-700 focus-within:border-amber-500 dark:focus-within:border-amber-400'
              }`}
            >
              <Mail size={18} className="ml-3 text-zinc-400" />
              <input
                ref={emailRef}
                value={email}
                onChange={(e) => onChangeEmail(e.target.value)}
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                data-testid="auth-input-email"
                className="flex-1 bg-transparent outline-none px-3 text-[15px] text-zinc-900 dark:text-white placeholder-zinc-400"
              />
              {mode === 'register' && emailStatus === 'checking' && (
                <Loader2 size={16} className="mr-3 animate-spin text-zinc-400" data-testid="auth-email-checking" />
              )}
              {mode === 'register' && emailStatus === 'available' && (
                <Check size={16} className="mr-3 text-emerald-500" data-testid="auth-email-available" />
              )}
              {mode === 'register' && (emailStatus === 'invalid' || emailStatus === 'taken') && (
                <AlertCircle size={16} className="mr-3 text-rose-500" data-testid="auth-email-error" />
              )}
            </div>
            {mode === 'register' && emailMsg && (
              <div
                className={`text-[11px] ${
                  emailStatus === 'available'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : emailStatus === 'invalid' || emailStatus === 'taken'
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-zinc-500 dark:text-zinc-400'
                }`}
                data-testid="auth-email-status"
              >
                {emailMsg}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Password</label>
            <div className="flex items-center h-11 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 focus-within:border-amber-500 dark:focus-within:border-amber-400 overflow-hidden">
              <Lock size={18} className="ml-3 text-zinc-400" />
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? 'text' : 'password'}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                placeholder="At least 6 characters"
                data-testid="auth-input-password"
                className="flex-1 bg-transparent outline-none px-3 text-[15px] text-zinc-900 dark:text-white placeholder-zinc-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="pr-3 pl-1 text-zinc-400 hover:text-zinc-600"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {/*
              Forgot-password link — shown only on the sign-in form
              since it doesn't apply to Create Account. Closes the
              modal and navigates to `/forgot-password`, matching the
              flow every other password-reset UX on the web uses.
            */}
            {mode === 'login' && (
              <div className="mt-1.5 text-right">
                <button
                  type="button"
                  onClick={() => { closeAuthModal(); navigate('/forgot-password'); }}
                  data-testid="auth-forgot-password-link"
                  className="text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </div>

          {/* Faith — simplified binary picker ("Non-Christian" /
              "Christian"). Sits directly above the Gender field so
              the prayer-writing hints stay clustered together at the
              bottom of the form. Optional; defaults to
              "non_christian" so we don't presume the user's tradition. */}
          {mode === 'register' && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Faith tradition (optional)</label>
              <select
                value={religion}
                onChange={(e) => setReligion(e.target.value)}
                data-testid="auth-input-religion"
                className="w-full h-11 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-3 text-[15px] text-zinc-900 dark:text-white focus:border-amber-500 dark:focus:border-amber-400 outline-none"
              >
                <option value="non_christian">Non-Christian</option>
                <option value="christian">Christian</option>
              </select>
              <p
                className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-relaxed pt-0.5"
                data-testid="auth-religion-hint"
              >
                Optional: steers the prayer writing assistance to match your voice.
              </p>
            </div>
          )}

          {/* Gender — moved to the bottom of the field stack (below
              Password, above Terms) so the Create-Account flow leads
              with essentials (name / handle / email / password) and
              only surfaces the Prayer-Assistance pronoun hint at the
              very end where it belongs contextually. */}
          {mode === 'register' && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Gender (optional)</label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                data-testid="auth-input-gender"
                className="w-full h-11 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-3 text-[15px] text-zinc-900 dark:text-white focus:border-amber-500 dark:focus:border-amber-400 outline-none"
              >
                {GENDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p
                className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-relaxed pt-0.5"
                data-testid="auth-gender-hint"
              >
                Optional: used by the prayer writing assistance to use correct pronouns.
              </p>
            </div>
          )}

          {error && (
            <div
              className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2 space-y-1.5"
              data-testid="auth-error"
            >
              <div>{error}</div>
              {showRegisterHint && (
                <button
                  type="button"
                  onClick={() => { setMode('register'); setShowRegisterHint(false); setError(''); }}
                  data-testid="auth-hint-register"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 dark:text-red-300 underline underline-offset-2 hover:opacity-80"
                >
                  Create an account with this email &rarr;
                </button>
              )}
            </div>
          )}

          {mode === 'register' && (
            <label
              className="flex items-start gap-2.5 cursor-pointer select-none px-1"
              data-testid="auth-accept-terms-row"
            >
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked);
                  if (e.target.checked && error) setError('');
                }}
                data-testid="auth-accept-terms-checkbox"
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 dark:border-zinc-600 text-amber-500 focus:ring-amber-400 accent-amber-500 flex-shrink-0"
              />
              <span className="text-xs text-zinc-600 dark:text-zinc-300 leading-snug">
                I accept the{' '}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); openTerms(); }}
                  data-testid="auth-legal-btn"
                  className="text-amber-600 dark:text-amber-400 font-medium hover:underline underline-offset-2 bg-transparent border-0 p-0 cursor-pointer"
                >
                  terms &amp; conditions
                </button>
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={submitting || (mode === 'register' && !acceptedTerms)}
            data-testid="auth-submit-btn"
            className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-[15px] flex items-center justify-center gap-2 transition-colors"
          >
            {submitting && <Loader2 size={18} className="animate-spin" />}
            {mode === 'login' ? 'Sign in' : 'Create account'}
          </button>

          <div className="text-center text-sm text-zinc-600 dark:text-zinc-400 pt-1">
            {mode === 'login' ? (
              <>
                New here?{' '}
                <button type="button" onClick={switchMode} data-testid="auth-switch-to-register" className="text-amber-600 dark:text-amber-400 font-medium hover:underline">
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button type="button" onClick={switchMode} data-testid="auth-switch-to-login" className="text-amber-600 dark:text-amber-400 font-medium hover:underline">
                  Sign in
                </button>
              </>
            )}
          </div>
        </form>
        )}
      </div>
      <SanctuaryModerationDialog
        open={!!moderationDialog}
        variant={moderationDialog?.variant}
        reason={moderationDialog?.reason}
        source={moderationDialog?.source}
        onClose={() => setModerationDialog(null)}
      />
    </div>
  );
};

export default AuthModal;
